import {ReferenceNodeType} from "~/enums/referenceNodeType";
import {
  IReferencePathNodeApiEndPoint,
  IReferencePathNodeObjectModel,
  IReferencePathNodeSpecific
} from '~/interfaces/referencePathInterfaces'
import { Logger } from "~/services/logger";
import { DatePathService } from "~/services/referencePathServices/datePathService";
import FormElementPathService from "~/services/referencePathServices/formElementPathService";
import FormPathService from "~/services/referencePathServices/formPathService";
import FormPaymentDataPathService from "~/services/referencePathServices/formPaymentDataPathService";
import ObjectModelPathService from "~/services/referencePathServices/objectModelPathService";
import { ObjectModelPropertyPathService } from "~/services/referencePathServices/objectModelPropertyPathService";
import ObjectModelRelationPathService from "~/services/referencePathServices/objectModelRelationPathService";
import ProcessFlowVariablePathService from "~/services/referencePathServices/processFlowVariablePathService";
import UserDataPathService from "~/services/referencePathServices/userDataPathService";
import WorkflowStepPathService from '~/services/referencePathServices/workflowStepPathService';
import { WorkflowStepDatePathService } from "./workflowStepDatePathService";
import ApiEndPointPathService from "~/services/referencePathServices/apiEndPointPathService";
import WorkflowStepActionPathService from "~/services/referencePathServices/workflowStepActionPathService";
import ApiDynamicInputPathService from '~/services/referencePathServices/apiDynamicInputPathService'
import ApiMessageTypePathService from '~/services/referencePathServices/apiMessageTypePathService'
import ApiConnectionPathService from '~/services/referencePathServices/apiConnectionPathService'
import {ObjectModelMetadataPathService} from "./ObjectModelMetadataPathService";
import {AccountRelationMetadataPathService} from "~/services/referencePathServices/accountRelationMetadataPathService";
import FormSubmissionMetadataPathService from "./formSubmissionMetadataPathService";
import {ProcessFlowMetadataPathService} from "~/services/referencePathServices/processFlowMetadataPathService";
import {WorkflowGeneratedCertificatePathService} from "~/services/referencePathServices/workflowGeneratedCertificatePathService";
import CertificateDynamicInputPathService from "~/services/referencePathServices/certificateDynamicInputPathService";
import CertificatePathService from "~/services/referencePathServices/certificatePathService";
import ApiHeaderPathService from "./apiHeaderPathService";
import ApiURLParameterPathService from "./apiURLParameterPathService";
import ObjectModelLinkedFormPathService from '~/services/referencePathServices/objectModelLinkedFormPathService';
import FormSectionPathService from '~/services/referencePathServices/formSectionPathService';

export default class ReferencePathServiceFactory {
  static create(node: IReferencePathNodeSpecific) {
    switch (node.reference_type) {
      case ReferenceNodeType.ProcessFlowInitiatorFirstName: // intentional fall-through
      case ReferenceNodeType.ProcessFlowInitiatorLastName: // intentional fall-through
      case ReferenceNodeType.ProcessFlowInitiatorEmail: // intentional fall-through
      case ReferenceNodeType.ProcessFlowTriggerTime:
        return new ProcessFlowMetadataPathService(node);
      case ReferenceNodeType.ProcessFlowVariable:
        return new ProcessFlowVariablePathService(node);
      case ReferenceNodeType.ObjectModel:
        return new ObjectModelPathService(node);
      case ReferenceNodeType.ObjectModelProperty:
        return new ObjectModelPropertyPathService(node);
      case ReferenceNodeType.ObjectModelRelation:
        const nodeIndex = node.parent.nodes.findIndex(n => n === node);
        const nextNode: IReferencePathNodeObjectModel = node.parent.nodes[nodeIndex + 1] as IReferencePathNodeObjectModel;

        if (nextNode && (nextNode.reference_type !== ReferenceNodeType.ObjectModel && nextNode.reference_type !== ReferenceNodeType.AccountRelationMetadata)) {
          Logger.errorOnce('ReferencePathNode#service', `Expected next reference node to match type ${ReferenceNodeType.ObjectModel} (ReferenceNodeType.ObjectModel), got type ${nextNode.reference_type}`);
        }

        return new ObjectModelRelationPathService(node, nextNode);
      case ReferenceNodeType.ObjectModelRecordMetadata:
        return new ObjectModelMetadataPathService(node);
      case ReferenceNodeType.ObjectModelLinkedForm:
        return new ObjectModelLinkedFormPathService(node);
      case ReferenceNodeType.AccountRelationMetadata:
        return new AccountRelationMetadataPathService(node);
      case ReferenceNodeType.Form:
        return new FormPathService(node);
      case ReferenceNodeType.FormSubmissionID:
        return new FormSubmissionMetadataPathService(node);
      case ReferenceNodeType.WorkflowStep:
        return new WorkflowStepPathService(node);
      case ReferenceNodeType.FormSection:
        return new FormSectionPathService(node);
      case ReferenceNodeType.WorkflowStepAction:
        return new WorkflowStepActionPathService(node);
      case ReferenceNodeType.WorkflowGeneratedCert:
        return new WorkflowGeneratedCertificatePathService(node);
      case ReferenceNodeType.FormElement:
        return new FormElementPathService(node);
      case ReferenceNodeType.FormPaymentsTotalCost: // intentional fallthrough
      case ReferenceNodeType.FormPaymentsType: // intentional fallthrough
      case ReferenceNodeType.FormPaymentsDate: // intentional fallthrough
      case ReferenceNodeType.FormPaymentsLineItem:
        return new FormPaymentDataPathService(node);
      case ReferenceNodeType.OriginalSubmitter:
      case ReferenceNodeType.AllCompleters:
      case ReferenceNodeType.StepCompleter:
      case ReferenceNodeType.RecordHolder:
        return new UserDataPathService(node);
      case ReferenceNodeType.FormSubmissionDate:
        return new DatePathService(node);
      case ReferenceNodeType.WorkflowStepStart: // intentional fallthrough
      case ReferenceNodeType.WorkflowStepEnd:
        return new WorkflowStepDatePathService(node);
      case ReferenceNodeType.ApiConnection:
        return new ApiConnectionPathService(node);
      case ReferenceNodeType.ApiEndpoint:
        return new ApiEndPointPathService(node);
      case ReferenceNodeType.ApiMessageType:
        const endpointNodeIndex = node.parent.nodes.findIndex(n => n === node);
        const endpointNode = node.parent.nodes[endpointNodeIndex - 1] as IReferencePathNodeApiEndPoint;

        return new ApiMessageTypePathService(endpointNode, node);
      case ReferenceNodeType.DynamicInput:
        return new ApiDynamicInputPathService(node);
      case ReferenceNodeType.Certificate:
        return new CertificatePathService(node);
      case ReferenceNodeType.CertificateDynamicInput:
        return new CertificateDynamicInputPathService(node);
      case ReferenceNodeType.ApiHeader:
        return new ApiHeaderPathService(node)
      case ReferenceNodeType.ApiURLParameter:
        return new ApiURLParameterPathService(node)
      default:
        Logger.errorOnce('ReferencePathServiceFactory#create', `Encountered unexpected ReferenceNodeType: ${(<any>node).reference_type}`);
        break;
    }
  }

}